import React, { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import T from 'prop-types';
import cx from 'classnames';
import { some } from 'lodash-es';

import InputText from '../../Inputs/InputText';

import { defaultDateFormat, format, isValid } from '../../../utils/date';

const month = 'month';
const day = 'day';
const year = 'year';

const inputNames = [month, day, year];

const inputPlaceholders = {
  [month]: 'M',
  [day]: 'D',
  [year]: 'YYYY',
};

const dateDigits = {
  [month]: 2,
  [day]: 2,
  [year]: 4,
};

const dateMin = {
  [month]: 1,
  [day]: 1,
  [year]: 1,
};

const maxYearOffset = 100;
const dateMax = {
  [month]: 12,
  [day]: 31,
  [year]: new Date().getFullYear() + maxYearOffset,
};

const dateCurrent = {
  [month]: new Date().getMonth() + 1,
  [day]: new Date().getDate(),
  [year]: new Date().getFullYear(),
};

const nextDateField = {
  [month]: day,
  [day]: year,
  [year]: null,
};

const prevDateField = {
  [month]: null,
  [day]: month,
  [year]: day,
};

const isUnfilled = dateValues => some(dateValues, (v, name) => {
  if (name === year) {
    return v.length !== dateDigits[name] || v.startsWith('0');
  }

  return v.length !== dateDigits[name];
});

const parseInputValue = (inputValue, type, { hasPaddedZeros }) => {
  if (!inputValue) return '';

  let validValue = inputValue.replace(/\D/g, '');
  if (hasPaddedZeros) validValue = validValue.replace(/^0+/, '');
  if (!validValue || validValue === '0'.repeat(dateDigits[type])) return '';

  return validValue.slice(0, dateDigits[type]);
};

const delay = (callback, timeout = 0) => setTimeout(callback, timeout);

const CustomDateInput = ({
  value,
  outputFormat = defaultDateFormat,
  focusOnMount = false,
  className,
  onChange,
}) => {
  const isDateChangeRef = useRef(false);
  const inputRefs = useRef({
    [month]: useRef(),
    [day]: useRef(),
    [year]: useRef(),
  });
  const padZerosRef = useRef({
    [month]: false,
    [day]: false,
    [year]: false,
  });

  const [dateValues, setDateValues] = useState({
    [month]: '',
    [day]: '',
    [year]: '',
  });

  const localValue = isUnfilled(dateValues) ? '' : inputNames.map(name => dateValues[name]).join('/');

  const populateDateFields = useCallback((dateValue) => {
    if (dateValue && isValid(dateValue)) {
      const date = format(dateValue, defaultDateFormat);
      const [m, d, y] = date.split('/');

      setDateValues({
        month: m,
        day: d,
        year: y,
      });
    }
  }, []);

  const handleDateChange = useCallback((inputValue, { target: { name } }) => {
    const parsedValue = parseInputValue(inputValue, name, {
      hasPaddedZeros: padZerosRef.current[name],
    });

    const shouldPadZeros = parsedValue && parsedValue.length < dateDigits[name] && !parsedValue.startsWith('0');
    padZerosRef.current[name] = shouldPadZeros;

    const adjustedValue = shouldPadZeros ? parsedValue.padStart(dateDigits[name], '0') : parsedValue;

    setDateValues((prevState) => {
      if (!shouldPadZeros && adjustedValue.length === dateDigits[name]) {
        inputRefs.current[nextDateField[name]]?.current.select();
      }

      if (adjustedValue !== prevState[name]) {
        isDateChangeRef.current = true;

        return ({
          ...prevState,
          [name]: adjustedValue,
        });
      }

      return prevState;
    });
  }, []);

  const handlePaste = useCallback((e) => {
    e.preventDefault();

    isDateChangeRef.current = true;
    const pastedText = e.clipboardData.getData('text');

    if (isValid(pastedText) && pastedText.length > dateDigits.year) {
      populateDateFields(pastedText);
      inputRefs.current[year].current.focus();
    } else {
      const { target } = e;
      const nextValue = `${target.value.substring(0, target.selectionStart)}${pastedText}${target.value.substring(target.selectionEnd)}`;

      handleDateChange(nextValue, { target });
    }
  }, []);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Backspace') {
      // If cursor is at the beginning and there's a previous input
      if (e.target.selectionEnd === 0 && prevDateField[e.target.name]) {
        e.preventDefault();
        const prevInput = inputRefs.current[prevDateField[e.target.name]].current;
        prevInput.focus();
      }
    }
    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      // If there's a previous input
      if (prevDateField[e.target.name]) {
        const prevInput = inputRefs.current[prevDateField[e.target.name]].current;
        prevInput.focus();
      } else {
        const currentInput = inputRefs.current[e.target.name].current;
        delay(() => currentInput.select());
      }
    }

    if (e.key === 'ArrowRight') {
      e.preventDefault();
      // If there's a next input
      if (nextDateField[e.target.name]) {
        const nextInput = inputRefs.current[nextDateField[e.target.name]].current;
        nextInput.focus();
      } else {
        const currentInput = inputRefs.current[e.target.name].current;
        delay(() => currentInput.select());
      }
    }

    // Counter behavior
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();

      const change = e.key === 'ArrowDown' ? -1 : 1;

      setDateValues((prevState) => {
        isDateChangeRef.current = true;

        const prevValue = prevState[e.target.name];
        let nextValue;

        if (prevValue.length) {
          nextValue = parseInt(prevValue, 10) + change;
          if (nextValue > dateMax[e.target.name]) {
            nextValue = dateMin[e.target.name];
          } else if (nextValue < dateMin[e.target.name]) {
            nextValue = dateMax[e.target.name];
          }
        } else {
          nextValue = dateCurrent[e.target.name];
        }

        nextValue = nextValue?.toString().padStart(dateDigits[e.target.name], '0') || '';

        padZerosRef.current[e.target.name] = false;
        if (e.target.name === year && nextValue.startsWith('0')) {
          padZerosRef.current[e.target.name] = true;
        }

        return ({
          ...prevState,
          [e.target.name]: nextValue,
        });
      });
    }
  }, []);

  const handleFocus = useCallback(({ target: { name } }) => {
    inputRefs.current[name].current.select();
  }, []);

  const handleSelect = useCallback(({
    target: { value: inputValue, name, selectionEnd, selectionStart },
  }) => {
    // If there is a partial selection, select the entire input
    if (selectionEnd !== selectionStart && selectionEnd - selectionStart < inputValue.length) {
      inputRefs.current[name].current.select();
    }
  }, []);

  // Effects
  useEffect(() => {
    if (focusOnMount) {
      delay(() => {
        inputRefs.current[month]?.current.select();
      });
    }
  }, []);

  // Populate date fields when the component mounts or when the value prop changes
  useEffect(() => {
    populateDateFields(value);
  }, [value]);

  useEffect(() => {
    if (isDateChangeRef.current) {
      onChange(localValue ? format(localValue, outputFormat) : '');
    }
  }, [localValue]);

  return (
    <div className={cx('custom-date-input', className)} role="group" aria-label="Date input">
      {inputNames.map((name, index) => (
        <Fragment key={name}>
          {index > 0 && <span className="separator">/</span>}
          <InputText
            ref={inputRefs.current[name]}
            value={dateValues[name]}
            name={name}
            placeholder={inputPlaceholders[name]}
            inputMode="numeric"
            className={cx(`${name}-input`)}
            onChange={handleDateChange}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            onFocus={handleFocus}
            onSelect={handleSelect}
          />
        </Fragment>
      ))}
    </div>
  );
};

CustomDateInput.propTypes = {
  value: T.string,
  outputFormat: T.string,
  focusOnMount: T.bool,
  className: T.string,
  onChange: T.func.isRequired,
};

export default CustomDateInput;
